import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"
import { useHapticFeedback } from "@/hooks/use-haptic"

const Dialog = DialogPrimitive.Root

const DialogTrigger = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Trigger>
>(({ onClick, ...props }, ref) => {
  const { triggerHaptic } = useHapticFeedback();

  const handleClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
    try {
      await triggerHaptic('selection');
      if (onClick) onClick(e);
    } catch (error) {
      console.warn('[Dialog] Haptic feedback failed:', error);
      if (onClick) onClick(e);
    }
  };

  return (
    <DialogPrimitive.Trigger
      ref={ref}
      onClick={handleClick}
      {...props}
    />
  );
});

DialogTrigger.displayName = DialogPrimitive.Trigger.displayName

const DialogPortal = DialogPrimitive.Portal

const DialogClose = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Close>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Close>
>(({ onClick, ...props }, ref) => {
  const { triggerHaptic } = useHapticFeedback();

  const handleClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
    try {
      await triggerHaptic('selection');
      if (onClick) onClick(e);
    } catch (error) {
      console.warn('[DialogClose] Haptic feedback failed:', error);
      if (onClick) onClick(e);
    }
  };

  return (
    <DialogPrimitive.Close
      ref={ref}
      onClick={handleClick}
      {...props}
    />
  );
});

DialogClose.displayName = DialogPrimitive.Close.displayName

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props}
  />
))

DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & { rtl?: boolean }
>(({ className, children, rtl, "aria-describedby": ariaDescribedby, ...props }, ref) => {
  // Determine document direction - inspect element or document for RTL
  const isRTL = rtl || 
    (typeof document !== 'undefined' && 
    (document.dir === 'rtl' || document.documentElement.getAttribute('dir') === 'rtl'));
  
  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          "fixed left-[50%] top-[50%] z-50 grid w-[calc(100%-32px)] max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-4 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] rounded-xl sm:rounded-xl sm:p-6",
          className
        )}
        aria-describedby={ariaDescribedby}
        {...props}
      >
        {children}
        <DialogPrimitive.Close 
          className={cn(
            "absolute top-3 right-3 sm:top-4 sm:right-4 rounded-full p-1.5",
            "text-primary transition-colors",
            "hover:text-primary/70",
            "focus:outline-none focus:ring-1 focus:ring-primary focus:ring-offset-1", 
            "disabled:pointer-events-none"
          )}
        >
          <X className="h-5 w-5" />
          <span className="sr-only">
            {isRTL ? "إغلاق" : "Close"}
          </span>
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPortal>
  )
})

DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-1.5 text-center mb-2",
      className
    )}
    {...props}
  />
)

DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:space-x-2 py-1 mt-2",
      className
    )}
    {...props}
  />
)

DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-none tracking-tight",
      className
    )}
    {...props}
  />
))

DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
))

DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}